// Full-bleed home hero: Shanghai skyline + sea, a diagonal sky-blue banner that
// wipes in from a corner (echoing the brochure cover), and a bilingual H1 with a
// "setting sail" mood + primary CTA. Owned by Agent D.
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { reducedMotion } from "../motion/variants";

interface HeroProps {
  /** Active locale prefix, e.g. "zh" / "en", for building links. */
  lang: string;
}

const ease = [0.16, 1, 0.3, 1] as const;
const dur = reducedMotion ? 0.001 : 0.7;

export default function Hero({ lang }: HeroProps) {
  const { t } = useTranslation("home");
  const isZh = lang === "zh";

  // zh-first: in Chinese mode the big bilingual title leads and the Latin
  // "SET SAIL …" caption follows below; in en mode the caption sits above.
  const fade = (y: number) => ({
    hidden: { opacity: 0, y: reducedMotion ? 0 : y },
    visible: { opacity: 1, y: 0, transition: { duration: dur, ease } },
  });
  const eyebrowEl = (
    <motion.p
      variants={fade(14)}
      className={`font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]${
        isZh ? " mt-5" : ""
      }`}
    >
      {t("hero.eyebrowEn")}
    </motion.p>
  );
  const titleEl = (
    <motion.h1
      variants={fade(18)}
      className={`font-zh text-[var(--text-4xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)] md:text-[var(--text-5xl)]${
        isZh ? "" : " mt-6"
      }`}
    >
      <span className="block">{t("hero.titleLine1")}</span>
      <span className="block text-[var(--brand-azure)]">{t("hero.titleLine2")}</span>
    </motion.h1>
  );

  return (
    <section className="relative isolate overflow-hidden bg-[var(--brand-tint-3)]">
      {/* Skyline + sea photograph, anchored to the bottom. */}
      <div className="absolute inset-0 -z-10">
        <img
          src="/images/skyline-hero.jpg"
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-bottom"
          loading="eager"
          decoding="async"
        />
        {/* White-to-transparent wash so the headline stays legible over the photo. */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/70 to-transparent" />
      </div>

      {/* Diagonal sky-blue banner wiping in from the top-right (cover motif). */}
      <motion.div
        aria-hidden="true"
        initial={{ x: reducedMotion ? 0 : "30%", opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: dur, ease }}
        className="pointer-events-none absolute -right-24 -top-24 -z-10 hidden h-[140%] w-[55%] bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-azure)] opacity-90 md:block"
        style={{ clipPath: "polygon(38% 0, 100% 0, 100% 100%, 0 100%)" }}
      />

      <div className="mx-auto flex min-h-[78vh] w-full max-w-6xl flex-col justify-center px-6 py-24">
        <motion.div
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: reducedMotion ? 0 : 0.12, delayChildren: 0.1 }}
          className="max-w-2xl"
        >
          {isZh ? (
            <>
              {titleEl}
              {eyebrowEl}
            </>
          ) : (
            <>
              {eyebrowEl}
              {titleEl}
            </>
          )}

          <motion.p
            variants={{
              hidden: { opacity: 0, y: reducedMotion ? 0 : 16 },
              visible: { opacity: 1, y: 0, transition: { duration: dur, ease } },
            }}
            className="mt-6 max-w-xl text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--ink)]/80"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            variants={{
              hidden: { opacity: 0, y: reducedMotion ? 0 : 16 },
              visible: { opacity: 1, y: 0, transition: { duration: dur, ease } },
            }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to={`/${lang}/contact`}
              className="inline-flex items-center rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-7 py-3.5 font-medium text-white shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--brand-azure)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
            >
              {t("hero.ctaPrimary")}
            </Link>
            <Link
              to={`/${lang}/services`}
              className="inline-flex items-center rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white/70 px-7 py-3.5 font-medium text-[var(--brand-azure-text)] transition-colors hover:border-[var(--brand-azure)] hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
            >
              {t("hero.ctaSecondary")}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
